import { useEffect, useRef, useState } from "react";
import { Filter } from "lucide-react";
import { radarI18nDe } from "@/components/radar/i18n";
import { useDirectorEnabled } from "@/hooks/useDirectorEnabled";
import {
  cloneFilterState,
  defaultFilterForRadarType,
} from "@/components/radar/types/filter";
import {
  filterBubbles,
  withoutDirectorSpecials,
} from "@/components/radar/utils/filterBubbles";
import type { RadarBubble, RadarFilterState } from "@/services/api/teacher";
import {
  DEFAULT_MAX_TIME_RANGE_DAYS,
  DEFAULT_TIME_RANGE_DAYS,
} from "@/utils/radarStorage";
import { TalkRadarFilterSheet } from "./TalkRadarFilterSheet";
import { readTalkFilter, writeTalkFilter } from "./talkRadarFilterStorage";
import { useTalkRadarFilterConfig } from "./useTalkRadarFilterConfig";

export function TalkRadarFilter({
  role,
  bubbles,
  directorEnabled,
  timeMin = 1,
  timeMax = DEFAULT_MAX_TIME_RANGE_DAYS,
  timeDays = DEFAULT_TIME_RANGE_DAYS,
  onTimeDays,
  locked,
  lockedState = null,
  onVisibleIds,
}: {
  role: "admin" | "teacher";
  bubbles: RadarBubble[];
  directorEnabled?: boolean;
  timeMin?: number;
  timeMax?: number;
  timeDays?: number;
  onTimeDays?: (days: number) => void;
  locked?: boolean;
  lockedState?: RadarFilterState | null;
  onVisibleIds?: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const config = useTalkRadarFilterConfig(role);
  const [days, setDays] = useState(
    () => readTalkFilter(role)?.timeDays ?? timeDays
  );
  const [filterState, setFilterState] = useState<RadarFilterState>(
    () =>
      readTalkFilter(role)?.filter ??
      cloneFilterState(defaultFilterForRadarType(role))
  );
  const ft = radarI18nDe.filter;
  const visibleCount = filterBubbles(bubbles, filterState, {
    applyTypeFilter: role === "teacher",
  }).length;
  const directorHook = useDirectorEnabled();
  const directorOn = directorEnabled ?? directorHook;
  const isLocked = locked ?? config.configLock;
  const minDays = config.configMin ?? timeMin;
  const maxDays = config.configMax ?? timeMax;
  const onVisibleIdsRef = useRef(onVisibleIds);
  onVisibleIdsRef.current = onVisibleIds;
  const lockedStateRef = useRef(lockedState);
  lockedStateRef.current = lockedState ?? config.lockedFilterSnapshot;
  const timeDaysRef = useRef(timeDays);
  timeDaysRef.current = timeDays;

  useEffect(() => {
    if (isLocked) {
      setFilterState(
        cloneFilterState(
          lockedStateRef.current ?? defaultFilterForRadarType(role)
        )
      );
      setDays(timeDaysRef.current);
      return;
    }
    const stored = readTalkFilter(role);
    setFilterState(
      stored?.filter ?? cloneFilterState(defaultFilterForRadarType(role))
    );
    setDays(stored?.timeDays ?? timeDaysRef.current);
  }, [isLocked, role]);

  useEffect(() => {
    if (!config.configReady || isLocked) return;
    writeTalkFilter(role, filterState, days);
  }, [config.configReady, isLocked, role, filterState, days]);

  useEffect(() => {
    if (!config.configReady || isLocked) return;
    const stored = readTalkFilter(role);
    if (stored?.timeDays != null && stored.timeDays !== timeDays)
      onTimeDays?.(stored.timeDays);
  }, [config.configReady, role, onTimeDays, timeDays, isLocked]);

  const reportedIds = useRef<string | null>(null);
  useEffect(() => {
    const applied = directorOn
      ? filterState
      : withoutDirectorSpecials(filterState);
    const visible = filterBubbles(bubbles, applied, {
      applyTypeFilter: role === "teacher",
    });
    const ids = visible.map((item) => item.id);
    const key = ids.join("\0");
    if (reportedIds.current === key) return;
    reportedIds.current = key;
    onVisibleIdsRef.current?.(ids);
  }, [bubbles, filterState, role, directorOn]);

  function setFilter(partial: Partial<RadarFilterState>) {
    if (isLocked) return;
    setFilterState((prev) => {
      const next = cloneFilterState(prev);
      for (const key of [
        "status",
        "zones",
        "types",
        "subjects",
        "faculties",
        "classes",
        "requesters",
      ] as const) {
        if (Array.isArray(partial[key])) next[key] = [...partial[key]] as never;
      }
      if (partial.specials)
        next.specials = { ...next.specials, ...partial.specials };
      if (typeof partial.activeOnly === "boolean")
        next.activeOnly = partial.activeOnly;
      return next;
    });
  }

  function resetFilter() {
    if (isLocked) return;
    setFilterState(cloneFilterState(defaultFilterForRadarType(role)));
  }

  return (
    <>
      <button
        type="button"
        className="talk-radar-filter"
        aria-label={ft.title}
        aria-expanded={open}
        onClick={() => setOpen(true)}
        style={{ borderRadius: "50%" }}
      >
        <Filter className="w-4 h-4" />
      </button>
      <TalkRadarFilterSheet
        open={open}
        onClose={() => setOpen(false)}
        filterState={filterState}
        setFilter={setFilter}
        resetFilter={resetFilter}
        isLocked={isLocked}
        role={role}
        incomingOff={config.incomingOff}
        bubbles={bubbles}
        visibleCount={visibleCount}
        directorEnabled={directorOn}
        minDays={minDays}
        maxDays={maxDays}
        days={days}
        onDays={(next) => {
          setDays(next);
          onTimeDays?.(next);
        }}
      />
    </>
  );
}
